import { R2DataService } from '../../../services/R2DataService.js';
import { IndexedDBService } from '../../../services/IndexedDBService.js';
import { WORKER_URL, CDN_URL } from '../../../js/config.js';
import { IndexedDBInitializer } from '../../../core/utils/indexedDBInitializer.js';
import { tagMappingService } from '../../../services/TagMappingService.js';
import { fetchCache } from '../../../js/utils/fetchCache.js';
import { normalizeGender } from '../../../js/utils/genderUtils.js';
import { getCurrentUserId } from '../../../js/utils/auth_utils.js';
import { fetchPrivateCharacters, clearPrivateCharactersCache } from '../../../js/utils/privateCharactersApi.js';
import { storage } from '../../../js/utils/storage.js';
import { hasPaidMembership } from '../components/inventory-utils.js';
import { characterIsNsfw, filterOutfitIdsForSfw } from '../../../js/utils/nsfwTags.js';
import { loadOutfitMappings, getOutfitMappings } from '../../../js/utils/outfitMappings.js';
import { mapShortenedFieldNames } from '../../../js/utils/characterFieldMaps.js';
import { computeCharacterRanks } from '../components/rank-utils.js';
import { isPlaceholderCharacterName, pickResolvedCharacterName } from '../components/character-name-utils.js';
import { getBackgroundService } from '../../../core/services/BackgroundService.js';
import { getUserFavoriteCharacters, toggleFavoriteCharacter } from './favoritesApi.js';
import { getMainSpritesOnly, clearSpritePreviewCache } from './spritePreview.js';
import { getScenarios as fetchScenariosList,
  getScenarioDetails as fetchScenarioDetails,
  getScenarioSprite as fetchScenarioSprite,
  getBatchImageUrls as fetchBatchImageUrls,
  getUserPrivateScenarios as fetchUserPrivateScenarios,
  clearScenarioFetchCaches
} from './scenarioFetch.js';
import {
  filterBlockedCharacters,
  getBlockedFingerprint
} from '../../moderation/blockedContentStore.js';

const genderKey = (c) => (c?.gender == null ? null : normalizeGender(c.gender));
const matchesMaleToggle = (c, male) => genderKey(c) === (male ? 'male' : 'female');
const matchesGenderFilter = (c, filters) => {
  if (filters.male !== undefined) return matchesMaleToggle(c, filters.male);
  return genderKey(c) === 'female';
};

export class RankingsService {
  static instance = null;

  static getInstance(rankingsState) {
    if (!RankingsService.instance) {
      RankingsService.instance = new RankingsService(rankingsState);
    } else if (rankingsState && rankingsState !== RankingsService.instance.rankingsState) {
      RankingsService.instance.rankingsState = rankingsState;
    }
    return RankingsService.instance;
  }

  constructor(rankingsState) {
    if (RankingsService.instance) return RankingsService.instance;

    this.rankingsState = rankingsState;
    this.initialized = false;
    this.characterOutfitsCache = new Map();
    this.publicCharacterNameById = new Map();
    this.publicCharacterNameCacheLoaded = false;
    this.characterCache = null;
    this.ongoingRequest = null;
    this.initPromise = null;
    this.staticTagMappingsPromise = null;
    this._r2Service = null;
    this._dbService = null;
    this._dbInitializer = null;

    RankingsService.instance = this;
  }

  get r2Service() {
    if (!this._r2Service) this._r2Service = new R2DataService();
    return this._r2Service;
  }

  get dbService() {
    if (!this._dbService) this._dbService = new IndexedDBService();
    return this._dbService;
  }

  get dbInitializer() {
    if (!this._dbInitializer) this._dbInitializer = new IndexedDBInitializer();
    return this._dbInitializer;
  }

  async init() {
    if (this.initialized) return;
    if (this.initPromise) {
      await this.initPromise;
      return;
    }

    this.initPromise = (async () => {
      await this.r2Service.init();

      const db = await this.dbInitializer.init();
      if (db) {
        this.dbService.db = db;
        const stores = this.dbInitializer.getStores();
        this.dbService.stores = {
          ...stores,
          tagMappings: stores.tagMappings || 'tag-mappings'
        };
        this.dbService.isInitialized = true;
      }

      await tagMappingService.init();
      this.initialized = true;
    })();

    try {
      await this.initPromise;
    } catch (error) {
      this.initPromise = null;
      throw error;
    }
  }

  async preloadDeferredMappings(lang = 'en') {
    if (this.staticTagMappingsPromise) {
      await this.staticTagMappingsPromise;
      return;
    }

    this.staticTagMappingsPromise = loadOutfitMappings(lang).finally(() => {
      this.staticTagMappingsPromise = null;
    });

    await this.staticTagMappingsPromise;
  }

  rememberPublicCharacterName(characterId, name) {
    if (characterId == null || characterId === '') return '';
    const key = String(characterId);
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (!trimmed || isPlaceholderCharacterName(trimmed, characterId)) return '';
    this.publicCharacterNameById.set(key, trimmed);
    return trimmed;
  }

  async ensurePublicCharacterNameCache() {
    if (this.publicCharacterNameCacheLoaded) return;

    await this.init();
    const rankings = await this.r2Service.getCharacterRankings(false);
    if (Array.isArray(rankings)) {
      for (const character of rankings) {
        this.rememberPublicCharacterName(character?.id, character?.name);
      }
    }
    this.publicCharacterNameCacheLoaded = true;
  }

  async getPublicCharacterNameById(characterId) {
    if (characterId == null || characterId === '') return '';

    const key = String(characterId);
    const cached = this.publicCharacterNameById.get(key);
    if (cached) return cached;

    await this.ensurePublicCharacterNameCache();
    const fromRankings = this.publicCharacterNameById.get(key);
    if (fromRankings) return fromRankings;

    try {
      const character = await this.r2Service.getCharacterById(characterId, false);
      return this.rememberPublicCharacterName(character?.id ?? characterId, character?.name);
    } catch {
      return '';
    }
  }

  async getOutfitName(outfitId) {
    const currentLang = window.getCurrentLanguage?.() || 'en';
    await loadOutfitMappings(currentLang);
    const outfitName = getOutfitMappings()?.[outfitId]?.name || `Outfit ${outfitId}`;
    return outfitName.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
  }

  getBackgroundUrl(locId, timeOfDay = 'night') {
    if (locId == null || locId === '') return `${CDN_URL}/images/backgrounds/default.webp`;
    return getBackgroundService().constructBackgroundUrl(Math.floor(Number(locId)), timeOfDay);
  }

  async getCharacters(options = {}) {
    await this.init();
    const { page = 1, limit = 15, itemsPerPage = limit, filters = {}, offset } = options;
    const startIndex = Number.isFinite(offset) ? Math.max(0, offset) : (page - 1) * itemsPerPage;

    const cacheKey = JSON.stringify({ startIndex, limit, filters, blocked: getBlockedFingerprint() });
    const now = Date.now();

    if (this.characterCache && this.characterCache.key === cacheKey &&
      (now - this.characterCache.timestamp) < 30000) {
      return this.characterCache.data;
    }

    if (this.ongoingRequest && this.ongoingRequest.key === cacheKey) {
      return this.ongoingRequest.promise;
    }

    const requestPromise = (async () => {
      try {
        const rankingsPromise = this.r2Service.getCharacterRankings(true);
        const detailsPromise = this.getAllCharactersDetails(true).catch(error => {
          console.warn('Failed to fetch character details for filtering:', error);
          return [];
        });

        let characters = await rankingsPromise;
        if (!characters?.length) {
          characters = [];
        }

        characters = computeCharacterRanks(characters);

        const detailedCharacters = await detailsPromise;
        const detailsById = new Map(detailedCharacters.map(dc => [String(dc.id), dc]));
        characters = characters.map(character => {
          const details = detailsById.get(String(character.id));
          return details ? { ...character, ...details } : character;
        });

        let filteredCharacters = [...characters];
        const premiumCharacterCount = characters.filter(c => Number(c.isP) === 1).length;

        // TODO: Hide premium-flagged characters server-side (omit from public CDN
        // rankings for free users) rather than relying on this frontend filter.
        // Keep creator-owned and private-list characters visible even for free users.
        if (!hasPaidMembership(storage.get('userData')) && !filters.private) {
          const currentUserId = getCurrentUserId();
          filteredCharacters = filteredCharacters.filter(c => {
            if (Number(c.isP) !== 1) return true;
            return !!(currentUserId && String(c.creator_user_id) === String(currentUserId));
          });
        }

        if (filters.nameFilter) {
          const nameFilter = filters.nameFilter.toLowerCase();
          filteredCharacters = filteredCharacters.filter(c =>
            c.name.toLowerCase().includes(nameFilter)
          );
        }

        filteredCharacters = filteredCharacters.filter((c) => matchesGenderFilter(c, filters));

        if (!filters.nsfw) {
          filteredCharacters = filteredCharacters.filter((c) => !characterIsNsfw(c));
        }

        if (!filters.private) {
          const targetStyle = filters.style === 'realism' ? 'realism' : 'anime';
          filteredCharacters = filteredCharacters.filter(c => {
            const charStyle = c.style === 'realism' ? 'realism' : 'anime';
            return charStyle === targetStyle;
          });
        }

        if (filters.favorite) {
          if (this.rankingsState && this.rankingsState.favoriteCharacterIds) {
            filteredCharacters = filteredCharacters.filter(c =>
              this.rankingsState.favoriteCharacterIds.includes(parseInt(c.id))
            );

            const userId = getCurrentUserId();
            if (userId) {
              let privateCharacters;
              try {
                privateCharacters = await this.getUserPrivateCharacters(userId);
              } catch (err) {
                console.warn('[RankingsService] Failed to fetch private characters for favorite merge:', err);
                privateCharacters = [];
              }
              const favoriteIdsSet = new Set(this.rankingsState.favoriteCharacterIds.map(id => parseInt(id)));
              const existingIds = new Set(filteredCharacters.map(c => parseInt(c.id)));
              let privateFavorites = privateCharacters
                .filter(c => favoriteIdsSet.has(parseInt(c.id)) && !existingIds.has(parseInt(c.id)))
                .map(c => ({ ...c, favorite: true, private: true }));
              if (filters.male !== undefined) {
                privateFavorites = privateFavorites.filter(c => matchesMaleToggle(c, filters.male));
              }
              if (!filters.nsfw) {
                privateFavorites = privateFavorites.filter(c => !characterIsNsfw(c));
              }
              filteredCharacters = [...filteredCharacters, ...privateFavorites];
            }
          } else {
            filteredCharacters = [];
          }
        }

        if (filters.private && !filters.favorite) {
          try {
            const userId = getCurrentUserId();
            if (userId) {
              filteredCharacters = await this.getUserPrivateCharacters(userId);
              if (filters.male !== undefined) {
                filteredCharacters = filteredCharacters.filter(c => matchesMaleToggle(c, filters.male));
              }
              if (!filters.nsfw) {
                filteredCharacters = filteredCharacters.filter(c => !characterIsNsfw(c));
              }
            } else {
              filteredCharacters = [];
            }
          } catch (error) {
            console.error('[RankingsService] Error fetching private characters:', error);
            filteredCharacters = [];
          }
        }

        filteredCharacters.sort((a, b) => (b.pop || 0) - (a.pop || 0));
        filteredCharacters = filterBlockedCharacters(filteredCharacters);

        const hasSpecialFilter = filters.private || filters.favorite || filters.male !== undefined;
        if (hasSpecialFilter) {
          filteredCharacters = filteredCharacters.map((character, index) => ({
            ...character,
            visualPosition: index
          }));
        }

        const paginatedCharacters = filteredCharacters.slice(startIndex, startIndex + itemsPerPage);

        const characterData = paginatedCharacters.map(c => ({
          id: c.id,
          bt: c.bt !== undefined && c.bt !== null ? parseInt(c.bt) || 603 : 603,
          gender: c.gender !== undefined ? c.gender : null
        }));

        const spriteUrlsMap = getMainSpritesOnly(characterData);

        const enhancedCharacters = paginatedCharacters.map(character => {
          const mainSprite = spriteUrlsMap[character.id]?.main || '/assets/images/kiss.webp';
          const altSprite = spriteUrlsMap[character.id]?.alternate || '/assets/images/kiss.webp';

          return {
            ...character,
            front_card_preview_url: mainSprite,
            back_card_preview_url: altSprite,
            image: mainSprite
          };
        });

        this.notifyCharacterDetailsUpdated(enhancedCharacters);

        // Guard: the 'characters' store uses `id` as keyPath, so a single record with a
        // missing/invalid id would otherwise abort the whole transaction and return zero results.
        const storableCharacters = enhancedCharacters.filter(c => {
          const hasValidId = c?.id != null && !Number.isNaN(Number(c.id));
          if (!hasValidId) console.warn('[RankingsService] Skipping character with invalid id for storage:', c?.name, c?.id);
          return hasValidId;
        });

        await this.dbService.storeData('characters', storableCharacters);

        const result = {
          data: enhancedCharacters,
          totalPages: Math.ceil(filteredCharacters.length / itemsPerPage),
          totalCharacters: filteredCharacters.length,
          currentPage: page,
          premiumCharacterCount
        };

        this.characterCache = {
          key: cacheKey,
          data: result,
          timestamp: Date.now()
        };

        return result;
      } finally {
        this.ongoingRequest = null;
      }
    })();

    this.ongoingRequest = { key: cacheKey, promise: requestPromise };

    return requestPromise;
  }

  clearCharacterListCache() {
    this.characterCache = null;
    this.ongoingRequest = null;
  }

  async getAllCharactersDetails(forceRefresh = false) {
    try {
      const detailedCharacters = await this.r2Service.getCharacterDetails(forceRefresh);

      if (!detailedCharacters?.length) {
        console.warn('No detailed characters data received');
        return [];
      }

      return detailedCharacters.map(character => ({
        ...mapShortenedFieldNames(character),
        id: String(character.id),
        gender: character.gender != null ? normalizeGender(character.gender) : null
      }));
    } catch (error) {
      console.error('Error fetching detailed character information:', error);
      return [];
    }
  }

  notifyCharacterDetailsUpdated(characters) {
    try {
      this.rankingsState?.updateCharacters?.(characters);

      window.dispatchEvent(new CustomEvent('characterDetailsUpdated', {
        detail: { characters }
      }));
    } catch (error) {
      console.error('Error notifying character details update:', error);
    }
  }

  async getCharacterDetails(characterInput, options = {}) {
    const forceRefresh = options.forceRefresh === true;
    const characterId = typeof characterInput === 'object' && characterInput?.id !== undefined
      ? characterInput.id
      : characterInput;

    if (!characterId || isNaN(parseInt(characterId))) {
      return { id: characterId, name: 'Unknown', image: '/assets/images/kiss.webp', rank: '?' };
    }

    await this.init();

    const mergeHangoutFields = (target, completeCharacter) => {
      if (!completeCharacter) return target;
      if (!target.tags && completeCharacter.tags) target.tags = completeCharacter.tags;
      if (completeCharacter.likes) target.likes = completeCharacter.likes;
      if (completeCharacter.dislikes) target.dislikes = completeCharacter.dislikes;
      if (completeCharacter.descr) target.descr = completeCharacter.descr || target.descr;
      if (completeCharacter.info && !target.info) target.info = completeCharacter.info;
      const hangoutName = pickResolvedCharacterName(completeCharacter, characterId);
      if (hangoutName) target.name = hangoutName;
      return target;
    };

    const resolveNameFromSources = (...sources) => {
      for (const source of sources) {
        const name = pickResolvedCharacterName(source, characterId);
        if (name) return name;
      }
      return '';
    };

    let fullCharacter = null;

    if (!forceRefresh && this.rankingsState?.characters) {
      const stateChar = this.rankingsState.characters.find(c =>
        c.id === characterId || c.id === String(characterId)
      );
      if (stateChar) {
        fullCharacter = {
          ...stateChar,
          name: resolveNameFromSources(stateChar) || `Character ${characterId}`
        };
      }
    }

    if (!fullCharacter) {
      let baseCharacter = typeof characterInput === 'object' ? characterInput : {};

      if (!forceRefresh && !pickResolvedCharacterName(baseCharacter, characterId)) {
        try {
          const cachedCharacter = await this.dbService.getById('characters', characterId);
          baseCharacter = cachedCharacter || {};
        } catch {
          baseCharacter = {};
        }
      }

      const isPrivateHint = baseCharacter.private === true || baseCharacter.rank === 'Private';

      if (isPrivateHint) {
        try {
          if (forceRefresh) clearPrivateCharactersCache();
          const completeCharacter = await fetchCache.fetchCharacterDetails(characterId, true);
          fullCharacter = {
            ...(completeCharacter || {}),
            ...baseCharacter,
            id: characterId,
            private: true,
            rank: 'Private',
            image: completeCharacter?.image || baseCharacter.image || '/assets/images/kiss.webp',
            name: resolveNameFromSources(completeCharacter, baseCharacter) || `Character ${characterId}`
          };
        } catch (error) {
          console.error('Error fetching private character details:', error);
          fullCharacter = {
            ...baseCharacter,
            id: characterId,
            private: true,
            rank: 'Private',
            image: baseCharacter.image || '/assets/images/kiss.webp',
            name: resolveNameFromSources(baseCharacter) || `Character ${characterId}`
          };
        }
      } else {
        try {
          const characterFromR2 = await this.r2Service.getCharacterById(characterId, forceRefresh);

          fullCharacter = {
            ...characterFromR2,
            ...baseCharacter,
            id: characterId,
            image: characterFromR2.image || baseCharacter.image || '/assets/images/kiss.webp',
            name: resolveNameFromSources(characterFromR2, baseCharacter) || `Character ${characterId}`,
            rank: baseCharacter.rank || characterFromR2.rank || null
          };

          if (!fullCharacter.tags || isPlaceholderCharacterName(fullCharacter.name, characterId)) {
            try {
              const completeCharacter = await fetchCache.fetchCharacterDetails(characterId, false);
              mergeHangoutFields(fullCharacter, completeCharacter);
            } catch (error) {
              console.warn('Error fetching complete character data:', error);
            }
          }
        } catch (error) {
          console.error('Error fetching character details:', error);
          fullCharacter = {
            ...baseCharacter,
            id: characterId,
            image: baseCharacter.image || '/assets/images/kiss.webp',
            name: resolveNameFromSources(baseCharacter) || `Character ${characterId}`,
            rank: baseCharacter.rank || null
          };
          try {
            const completeCharacter = await fetchCache.fetchCharacterDetails(characterId, false);
            fullCharacter = {
              ...fullCharacter,
              ...(completeCharacter || {}),
              id: characterId,
              image: completeCharacter?.image || fullCharacter.image,
              name: resolveNameFromSources(completeCharacter, baseCharacter) || fullCharacter.name
            };
          } catch (hangoutError) {
            console.warn('Error fetching complete character data:', hangoutError);
          }
        }
      }
    }

    if (isPlaceholderCharacterName(fullCharacter?.name, characterId)) {
      const isPrivateHint = fullCharacter?.private === true
        || fullCharacter?.rank === 'Private'
        || (typeof characterInput === 'object' && (characterInput.private === true || characterInput.rank === 'Private'));
      try {
        const completeCharacter = await fetchCache.fetchCharacterDetails(characterId, isPrivateHint);
        mergeHangoutFields(fullCharacter, completeCharacter);
      } catch (error) {
        console.warn('Error fetching character name from hangout details:', error);
      }
    }

    const inputName = typeof characterInput === 'object'
      ? pickResolvedCharacterName(characterInput, characterId)
      : '';
    if (isPlaceholderCharacterName(fullCharacter?.name, characterId) && inputName) {
      fullCharacter.name = inputName;
    }

    if (!fullCharacter.name) {
      fullCharacter.name = `Character ${characterId}`;
    }

    if (this.dbService.db?.objectStoreNames?.contains('characterDetails')) {
      await this.dbService.storeData('characterDetails', fullCharacter);
    }

    return fullCharacter;
  }

  async getCharacterOutfits(characterId) {
    if (!characterId) return null;
    const cacheKey = `character_outfits_${characterId}`;
    const cached = this.characterOutfitsCache.get(cacheKey);
    if (cached) return filterOutfitIdsForSfw(cached);

    try {
      const response = await fetch(
        `${WORKER_URL}/character/outfits?character_id=${encodeURIComponent(characterId)}`
      );
      if (!response.ok) return null;
      const ids = await response.json();
      if (!Array.isArray(ids)) return null;
      const outfitSet = new Set(ids.map(id => parseInt(id, 10)));
      this.characterOutfitsCache.set(cacheKey, outfitSet);
      return filterOutfitIdsForSfw(outfitSet);
    } catch {
      return null;
    }
  }

  async getScenarios() {
    await this.init();
    return fetchScenariosList(this.rankingsState);
  }

  async getScenarioDetails(scenarioId) {
    return fetchScenarioDetails(scenarioId, this.rankingsState);
  }

  async getScenarioSprite(characterId, scenarioId, fallbackImage = '/assets/images/kiss.webp', outfitId = null) {
    return fetchScenarioSprite(characterId, scenarioId, fallbackImage, outfitId, this.rankingsState);
  }

  async getBatchImageUrls(ids) {
    return fetchBatchImageUrls(ids, this._dbService);
  }

  async getUserFavoriteCharacters(userId) {
    return getUserFavoriteCharacters(userId);
  }

  async toggleFavoriteCharacter(userId, characterId) {
    return toggleFavoriteCharacter(userId, characterId);
  }

  clearPrivateCharactersCache() {
    clearScenarioFetchCaches();
    clearPrivateCharactersCache();
  }

  async clearUserScopedCacheOnLogout() {
    this.clearPrivateCharactersCache();
    this.characterCache = null;
    this.ongoingRequest = null;
    clearSpritePreviewCache();
    this.characterOutfitsCache.clear();
    this.publicCharacterNameById.clear();
    this.publicCharacterNameCacheLoaded = false;

    try {
      fetchCache.clear();
      this.rankingsState.updateCharacters([]);
      this.rankingsState.setScenarios([]);
      if (!this.dbService) return;
      if (!this.dbService.db) await this.dbService.init?.();
      const db = this.dbService.db;
      if (!db) return;

      const storesToClear = [
        this.dbService.stores?.characters,
        this.dbService.stores?.characterDetails,
        this.dbService.stores?.imageUrls,
        this.dbService.stores?.scenarios,
        this.dbService.stores?.scenarioImages,
        'metadata'
      ].filter(Boolean);

      const uniqueStores = [...new Set(storesToClear)];
      const existingStores = uniqueStores.filter(storeName => db.objectStoreNames.contains(storeName));
      await Promise.all(existingStores.map(storeName => this.dbService.clearStore(storeName)));
    } catch (error) {
      console.warn('[RankingsService] Failed to clear user-scoped cache:', error);
    }
  }

  async getUserPrivateCharacters(userId) {
    if (!userId) {
      throw new Error('User ID is required');
    }
    return fetchPrivateCharacters();
  }

  async getUserPrivateScenarios(userId) {
    return fetchUserPrivateScenarios(userId);
  }

}
